"use client";

import { Badge, TrendingUp } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  getAuctionBiddingDetails,
  type AuctionDetails,
} from "@/lib/bids/bidding";

import { BidHistory, type BidsHistoryBids } from "./BidHistory";

interface BidHistoryModalProps {
  auction: AuctionDetails;
  bids?: BidsHistoryBids[];
  trigger: React.ReactNode;
  bidAction?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function BidHistoryModal({
  auction,
  bids,
  trigger,
  bidAction,
  open,
  onOpenChange,
}: BidHistoryModalProps) {
  const biddingDetails = getAuctionBiddingDetails(auction, bids);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="w-fit max-w-[90vw]">
        <DialogTitle className="">Bid history</DialogTitle>
        <DialogDescription className="">
          <div className="space-y-2 text-sm text-muted-foreground">
            <span>View the complete bid history for this listing.</span>
            <div className="flex items-center justify-between gap-4">
              <span>
                Next Bid: $
                {biddingDetails.minimumNextBidAmount.toLocaleString()}
              </span>
              <span>
                {biddingDetails.bidCount > 0 ? (
                  <span>
                    {biddingDetails.bidCount} bid
                    {biddingDetails.bidCount > 1 ? "s" : ""}
                  </span>
                ) : (
                  <span>No bids yet</span>
                )}
              </span>
              {biddingDetails.bidIncrease.greaterThan(0) && (
                <div className="flex items-center gap-1">
                  <Badge className="gap-1">
                    <TrendingUp className="h-4 w-4" />
                  </Badge>
                  <span>+${biddingDetails.bidIncrease.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </DialogDescription>
        <BidHistory auction={auction} bids={bids} />
        {bidAction && (
          <div className="flex justify-end">
            {/* Close the history modal before triggering the bid action to avoid nested dialog aria-hidden focus issues */}
            <DialogClose asChild>{bidAction}</DialogClose>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
